// REBUILD TRIGGER: 2025/12/22 19:37:13
import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import { AnimatedBackground, ModernCard, ModernButton, ModernStatCard, ModernInput, ModernBadge } from './ui/modern';
import Modal from './ui/Modal';

function BranchDetails({ user }) {
  console.log("BRANCH_DETAILS_V2_WITH_LABELS");
  const { branchId } = useParams();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const { selected, setSelected } = useContext(SelectedCompanyContext);
  const isManager = user?.role === 'Manager';
  const isAdmin = user?.role === 'Admin';
  const canEditDelete = isAdmin || isManager;
  const [services, setServices] = useState([]);
  const [detailModal, setDetailModal] = useState({ open: false, type: '', title: '' });
  const [detailRows, setDetailRows] = useState([]);
  const [detailSubtitle, setDetailSubtitle] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [sectorId, setSectorId] = useState(null);

  const entityDataToValues = (data) => {
    const values = {};
    (data || []).forEach((item) => {
      if (item.valueText != null) values[item.fieldKey] = item.valueText;
      else if (item.valueNumber != null) values[item.fieldKey] = item.valueNumber;
      else if (item.valueBoolean != null) values[item.fieldKey] = item.valueBoolean;
      else if (item.valueDate != null) values[item.fieldKey] = item.valueDate.slice(0, 10);
      else if (item.valueJson != null) values[item.fieldKey] = item.valueJson;
    });
    return values;
  };

  const getEntityTitle = (code, values, fallback) => {
    if (code === 'branch_user') return values.userName || values.name || fallback;
    if (code === 'branch_device') return values.deviceName || values.name || fallback;
    if (code === 'firewall') return values.firewallName || values.name || fallback;
    return fallback;
  };

  const getEntitySubtitle = (code, values) => {
    if (code === 'branch_user') return values.userEmail || values.email || '';
    if (code === 'branch_device') return values.ipAddress || values.ip || '';
    return '';
  };

  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const response = await api.get(`/branches/${branchId}`);
        setBranch(response.data);
        setFormData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching branch details:', error);
        setLoading(false);
      }
    };
    fetchBranch();

    // fetch tasks for this branch to list selected services
    const fetchServices = async () => {
      try {
        const res = await api.get(`/tasks/company/${branchId}`).catch(()=>({data:[]}));
        const BRANCH_SERVICES = ['Install MiBOT Agent','Install Managed AV','Setup Office 365 / Mail','Setup OneDrive','Install CyberFox','Install Acronis Backup Agent'];
        const normalize = str=>str.replace(/^(Install|Setup)\s+/i,'').trim().toLowerCase();
        const svcSet = new Set(res.data.filter(t=>t.branchId===parseInt(branchId)).map(t=>normalize(t.type)));
        const activeList = BRANCH_SERVICES.filter(s=>svcSet.has(normalize(s)));
        setServices(activeList);
      }catch(e){console.error('fetch services',e);}  
    };
    fetchServices();
  }, [branchId]);

  useEffect(() => {
    if (selected?.sectorId) {
      setSectorId(selected.sectorId);
      return;
    }
    if (branch?.companyId) {
      api.get(`/companies/${branch.companyId}`)
        .then((res) => setSectorId(res.data?.sectorId || null))
        .catch((err) => console.error('fetch company sector', err));
    }
  }, [selected?.sectorId, branch?.companyId]);

  const handleBack = () => {
    navigate(-1); // Navigate back to the previous page
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseInt(value) || 0 });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/branches/${branchId}`, formData);
      setBranch(response.data);
      setEditMode(false);
      alert('Branch updated successfully!');
    } catch (error) {
      console.error('Error updating branch:', error);
      alert('Error updating branch. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this branch? This action cannot be undone.')) {
      try {
        await api.delete(`/branches/${branchId}`);
        alert('Branch deleted successfully!');
        // remove branch from selected company context (if loaded)
        if (selected && selected.Branches) {
          setSelected({ ...selected, Branches: selected.Branches.filter(b => b.id !== Number(branchId)) });
        }
        // Navigate back to the company details page which will automatically refresh its branch list
        if (selected) {
          navigate(`/company/${selected.id}`, { replace: true });
        } else {
          navigate('/companies', { replace: true });
        }
      } catch (error) {
        console.error('Error deleting branch:', error);
        alert('Error deleting branch. Please try again.');
      }
    }
  };

  const openDetailModal = async (type) => {
    setDetailModal({ open: true, type, title: '' });
    setDetailRows([]);
    setDetailSubtitle('');
    setDetailLoading(true);
    try {
      if (type === 'servers') {
        const res = await api.get(`/companies/branch/${branchId}/servers`);
        setDetailRows(res.data || []);
        setDetailModal({ open: true, type, title: 'Servers' });
        return;
      }

      const codeMap = {
        users: 'branch_user',
        devices: 'branch_device',
        networkDevices: 'branch_device',
        firewall: 'firewall',
      };
      const code = codeMap[type];
      if (!code || !sectorId) {
        setDetailModal({ open: true, type, title: 'Details' });
        setDetailSubtitle('No configuration found.');
        return;
      }

      const typeRes = await api.get('/entity-types', { params: { sectorId, code } });
      const entityType = (typeRes.data || [])[0];
      if (!entityType) {
        setDetailModal({ open: true, type, title: 'Details' });
        setDetailSubtitle('No configuration found.');
        return;
      }

      const rowsRes = await api.get('/entity-instances', {
        params: { entityTypeId: entityType.id, branchId },
      });

      let rows = rowsRes.data || [];
      if (type === 'networkDevices') {
        const filtered = rows.filter((row) => {
          const values = entityDataToValues(row.EntityData || []);
          const marker = String(
            values.deviceType || values.deviceCategory || values.type || values.category || ''
          ).toLowerCase();
          return marker.includes('network');
        });
        if (filtered.length) {
          rows = filtered;
        } else {
          setDetailSubtitle('Showing all devices (no network flag found).');
        }
      }

      const titleMap = {
        users: 'Users',
        devices: 'Devices',
        networkDevices: 'Network Devices',
        firewall: 'Firewall',
      };
      setDetailRows(rows);
      setDetailModal({ open: true, type, title: titleMap[type] || 'Details' });
    } catch (err) {
      console.error('detail fetch', err);
      setDetailSubtitle('Failed to load details.');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white text-xl">Loading branch details...</div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white text-xl">Branch not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
      <AnimatedBackground />
      
      {/* Header Section */}
      <ModernCard className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {branch.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">{branch.name}</h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-slate-400">Branch Details & Infrastructure</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <ModernButton variant="glass" onClick={handleBack} className="w-full sm:w-auto">
              ← Back
            </ModernButton>
            {canEditDelete && (
              <>
                <ModernButton variant="gradient" onClick={handleEditToggle} className="w-full sm:w-auto">
                  {editMode ? '✕ Cancel' : '✏️ Edit Branch'}
                </ModernButton>
                <ModernButton 
                  variant="danger" 
                  onClick={handleDelete}
                  className="w-full sm:w-auto"
                >
                  🗑️ Delete
                </ModernButton>
              </>
            )}
          </div>
        </div>
      </ModernCard>

      {/* Stats Overview */}
      {!editMode ? (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <ModernStatCard
            title="Users"
            value={branch.numberOfUsers || 0}
            icon="👥"
            color="blue"
            onClick={() => openDetailModal('users')}
          />
          <ModernStatCard
            title="Devices"
            value={branch.numberOfDevices || 0}
            icon="💻"
            color="violet"
            onClick={() => openDetailModal('devices')}
          />
          <ModernStatCard
            title="Network Devices"
            value={branch.numberOfNetworkDevices || 0}
            icon="🌐"
            color="blue"
            onClick={() => openDetailModal('networkDevices')}
          />
          <ModernStatCard
            title="Servers"
            value={branch.numberOfServers || 0}
            icon="🖥️"
            color="emerald"
            onClick={() => openDetailModal('servers')}
          />
          <ModernStatCard
            title="Firewall"
            value={branch.hasFirewall ? "Yes" : "No"}
            icon="🛡️"
            color={branch.hasFirewall ? "emerald" : "red"}
            onClick={() => openDetailModal('firewall')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Editable Users Card */}
          <ModernCard className="bg-blue-50 dark:bg-gradient-to-br dark:from-blue-900/30 dark:to-blue-800/20 border-blue-500/30">
            <div className="text-center">
              <div className="text-4xl mb-2">👥</div>
              <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">Users</div>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfUsers: Math.max(0, (formData.numberOfUsers || 0) - 1) })}
                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-white flex items-center justify-center transition-colors"
                >
                  −
                </button>
                <span className="text-3xl font-bold text-gray-900 dark:text-white min-w-[60px]">{formData.numberOfUsers || 0}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfUsers: (formData.numberOfUsers || 0) + 1 })}
                  className="w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </ModernCard>

          {/* Editable Devices Card */}
          <ModernCard className="bg-purple-50 dark:bg-gradient-to-br dark:from-purple-900/30 dark:to-purple-800/20 border-purple-500/30">
            <div className="text-center">
              <div className="text-4xl mb-2">💻</div>
              <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">Devices</div>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfDevices: Math.max(0, (formData.numberOfDevices || 0) - 1) })}
                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-white flex items-center justify-center transition-colors"
                >
                  −
                </button>
                <span className="text-3xl font-bold text-gray-900 dark:text-white min-w-[60px]">{formData.numberOfDevices || 0}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfDevices: (formData.numberOfDevices || 0) + 1 })}
                  className="w-8 h-8 rounded-lg bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </ModernCard>

          {/* Editable Network Devices Card */}
          <ModernCard className="bg-teal-50 dark:bg-gradient-to-br dark:from-teal-900/30 dark:to-teal-800/20 border-teal-500/30">
            <div className="text-center">
              <div className="text-4xl mb-2">🌐</div>
              <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">Network Devices</div>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfNetworkDevices: Math.max(0, (formData.numberOfNetworkDevices || 0) - 1) })}
                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-white flex items-center justify-center transition-colors"
                >
                  −
                </button>
                <span className="text-3xl font-bold text-gray-900 dark:text-white min-w-[60px]">{formData.numberOfNetworkDevices || 0}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfNetworkDevices: (formData.numberOfNetworkDevices || 0) + 1 })}
                  className="w-8 h-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </ModernCard>

          {/* Editable Servers Card */}
          <ModernCard className="bg-emerald-50 dark:bg-gradient-to-br dark:from-emerald-900/30 dark:to-emerald-800/20 border-emerald-500/30">
            <div className="text-center">
              <div className="text-4xl mb-2">🖥️</div>
              <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">Servers</div>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfServers: Math.max(0, (formData.numberOfServers || 0) - 1) })}
                  className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-white flex items-center justify-center transition-colors"
                >
                  −
                </button>
                <span className="text-3xl font-bold text-gray-900 dark:text-white min-w-[60px]">{formData.numberOfServers || 0}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, numberOfServers: (formData.numberOfServers || 0) + 1 })}
                  className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </ModernCard>

          {/* Editable Firewall Toggle Card */}
          <ModernCard className={`${formData.hasFirewall ? 'bg-green-50 dark:bg-gradient-to-br dark:from-green-900/30 dark:to-green-800/20 border-green-500/30' : 'bg-red-50 dark:bg-gradient-to-br dark:from-red-900/30 dark:to-red-800/20 border-red-500/30'}`}>
            <div className="text-center">
              <div className="text-4xl mb-2">🛡️</div>
              <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">Firewall</div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hasFirewall: !formData.hasFirewall })}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  formData.hasFirewall
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {formData.hasFirewall ? '✓ Enabled' : '✕ Disabled'}
              </button>
            </div>
          </ModernCard>
        </div>
      )}
      {editMode ? (
        <ModernCard>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edit Branch Information</h3>
          <form onSubmit={handleUpdate}>
            {/* Basic Information Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-white/10">
                📋 Basic Information
              </h4>
              <div className="space-y-4">
                <ModernInput
                  label="Branch Name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  required
                />
                <ModernInput
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Email Address"
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-white/10">
                📍 Address Details
              </h4>
              <div className="space-y-4">
                <ModernInput
                  label="Street Address"
                  name="address"
                  value={formData.address || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="City"
                  name="city"
                  value={formData.city || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Province"
                  name="province"
                  value={formData.province || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode || ''}
                  onChange={handleInputChange}
                />
                <ModernInput
                  label="Country"
                  name="country"
                  value={formData.country || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Infrastructure Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-white/10">
                🔧 Infrastructure Details
              </h4>
              <div className="space-y-4">
                <ModernInput
                  label="Firewall"
                  name="firewall"
                  value={formData.firewall || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., Fortinet, Sophos, etc."
                />
                <ModernInput
                  label="Number of Users"
                  type="number"
                  name="numberOfUsers"
                  value={formData.numberOfUsers || 0}
                  onChange={handleNumberInputChange}
                  min="0"
                />
                <ModernInput
                  label="Number of Devices"
                  type="number"
                  name="numberOfDevices"
                  value={formData.numberOfDevices || 0}
                  onChange={handleNumberInputChange}
                  min="0"
                />
                <ModernInput
                  label="Number of Network Devices"
                  type="number"
                  name="numberOfNetworkDevices"
                  value={formData.numberOfNetworkDevices || 0}
                  onChange={handleNumberInputChange}
                  min="0"
                />
                <ModernInput
                  label="Number of Servers"
                  type="number"
                  name="numberOfServers"
                  value={formData.numberOfServers || 0}
                  onChange={handleNumberInputChange}
                  min="0"
                />
              </div>
            </div>

            <ModernButton type="submit" variant="gradient" className="w-full">
              💾 Update Branch
            </ModernButton>
          </form>
        </ModernCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Branch Information */}
          <ModernCard>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🏢</span>
              Branch Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Address</div>
                <div className="text-gray-900 dark:text-white">
                  {branch.address || 'N/A'}, {branch.city || 'N/A'}, {branch.province || 'N/A'}, {branch.postalCode || 'N/A'}, {branch.country || 'N/A'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Contact Person</div>
                  <div className="text-gray-900 dark:text-white">{branch.contactPerson || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Contact Number</div>
                  <div className="text-gray-900 dark:text-white">{branch.contactNumber || 'N/A'}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Email Address</div>
                <div className="text-gray-900 dark:text-white">{branch.emailAddress || 'N/A'}</div>
              </div>
            </div>
          </ModernCard>

          {/* Infrastructure Information */}
          <ModernCard>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🔧</span>
              Infrastructure Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Firewall</div>
                <div className="text-gray-900 dark:text-white">{branch.firewall || 'N/A'}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Users</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">{branch.numberOfUsers || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Devices</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">{branch.numberOfDevices || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Network Devices</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">{branch.numberOfNetworkDevices || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-1">Servers</div>
                  <div className="text-gray-900 dark:text-white text-2xl font-bold">{branch.numberOfServers || 0}</div>
                </div>
              </div>
            </div>
          </ModernCard>

          {/* Services (if any) */}
          {services.length > 0 && (
            <ModernCard className="lg:col-span-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span>⚙️</span>
                Selected Services
              </h3>
              <div className="flex flex-wrap gap-2">
                {services.map(s => (
                  <ModernBadge key={s} status="completed">
                    {s}
                  </ModernBadge>
                ))}
              </div>
            </ModernCard>
          )}
        </div>
      )}

      <Modal
        isOpen={detailModal.open}
        onClose={() => setDetailModal({ open: false, type: '', title: '' })}
        title={`${detailModal.title} (view only)`}
        size="3xl"
      >
        {detailLoading ? (
          <div className="text-gray-600 dark:text-slate-400">Loading...</div>
        ) : detailRows.length === 0 ? (
          <div className="text-gray-600 dark:text-slate-400">
            {detailSubtitle || 'No records found.'}
          </div>
        ) : (
          <div className="space-y-3">
            {detailSubtitle && (
              <div className="text-xs text-gray-500 dark:text-slate-400">{detailSubtitle}</div>
            )}
            {detailModal.type === 'servers' ? (
              detailRows.map((srv) => (
                <div key={srv.id} className="border border-gray-200 dark:border-white/10 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {srv.serverName || 'Unnamed server'}
                    </div>
                    <ModernBadge variant={srv.completed ? 'success' : 'warning'}>
                      {srv.completed ? 'Completed' : 'In Progress'}
                    </ModernBadge>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">
                    {srv.ipAddress || '—'}
                  </div>
                </div>
              ))
            ) : (
              detailRows.map((row) => {
                const values = entityDataToValues(row.EntityData || []);
                const title = getEntityTitle(
                  detailModal.type === 'users'
                    ? 'branch_user'
                    : detailModal.type === 'firewall'
                      ? 'firewall'
                      : 'branch_device',
                  values,
                  row.displayName || `Record #${row.id}`
                );
                const subtitle = getEntitySubtitle(
                  detailModal.type === 'users'
                    ? 'branch_user'
                    : detailModal.type === 'firewall'
                      ? 'firewall'
                      : 'branch_device',
                  values
                );
                return (
                  <div key={row.id} className="border border-gray-200 dark:border-white/10 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{title}</div>
                        {subtitle && (
                          <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{subtitle}</div>
                        )}
                      </div>
                      <ModernBadge variant={row.completed ? 'success' : 'warning'}>
                        {row.completed ? 'Completed' : 'In Progress'}
                      </ModernBadge>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default BranchDetails; 
// UNIQUE BUILD ID: d75c94fb-f9c7-4e7f-8a19-68dc08b8fa4c
// This comment forces webpack to generate a new hash

